import { useEffect, useMemo, useState } from "react";
import { LookupListEditor } from "@/components/LookupListEditor";
import { CollapsiblePanel } from "@/components/CollapsiblePanel";
import { MessageSquareText, Info, Copy, Check, Play, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

/**
 * 🆕 09/07/2026 — Gestione Modelli Messaggi
 * Percorso: /admin/message-templates
 * Consente di scrivere e salvare testi predefiniti per messaggi al cliente
 * (email / WhatsApp), sia generici che collegati a polizze, con placeholder che il
 * sistema sostituisce automaticamente al momento dell'invio.
 */

const CLIENT_PLACEHOLDERS = [
  { code: "{cliente}", desc: "Nome e cognome del cliente" },
  { code: "{agenzia}", desc: "Nome/ragione sociale della tua agenzia" },
  { code: "{telefono_agenzia}", desc: "Telefono agenzia" },
  { code: "{indirizzo_agenzia}", desc: "Indirizzo completo dell'agenzia" },
];

const POLICY_PLACEHOLDERS = [
  ...CLIENT_PLACEHOLDERS,
  { code: "{polizza}", desc: "Numero polizza" },
  { code: "{compagnia}", desc: "Compagnia assicurativa" },
  { code: "{data_scadenza}", desc: "Data di scadenza della polizza" },
  { code: "{tipo_polizza}", desc: "Tipologia polizza (auto/moto/casa…)" },
];

const CLAIM_PLACEHOLDERS = [
  ...CLIENT_PLACEHOLDERS,
  { code: "{polizza}", desc: "Numero polizza" },
  { code: "{compagnia}", desc: "Compagnia assicurativa" },
  { code: "{data_sinistro}", desc: "Data dell'evento sinistro" },
];

function PlaceholderChip({ code, desc }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      toast.success(`${code} copiato negli appunti`);
      setTimeout(() => setCopied(false), 1600);
    } catch { toast.error("Impossibile copiare"); }
  };
  return (
    <button
      type="button"
      onClick={copy}
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-mono transition-colors ${
        copied ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-indigo-300 bg-indigo-50 text-indigo-800 hover:bg-indigo-100"
      }`}
      title={`Clicca per copiare • ${desc}`}
    >
      {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
      {code}
    </button>
  );
}

function PlaceholdersLegend({ items, title = "Placeholder disponibili" }) {
  return (
    <div className="rounded-lg border border-indigo-200 bg-indigo-50/40 px-3 py-2 mb-3" data-testid="placeholders-legend">
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-semibold text-indigo-900 mb-1.5">
        <Info className="h-3.5 w-3.5" /> {title}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {items.map((p) => <PlaceholderChip key={p.code} {...p} />)}
      </div>
      <p className="text-[10.5px] text-indigo-800/70 mt-1.5">
        Clicca su un placeholder per copiarlo. Al momento dell&apos;invio il sistema sostituisce
        automaticamente ogni segnaposto con i dati reali del cliente / polizza.
      </p>
    </div>
  );
}

// 🆕 Anteprima rapida: seleziona modello + cliente (+ polizza) e vedi il testo finale renderizzato.
function QuickTestPanel() {
  const [listKey, setListKey] = useState("policy_message_templates");
  const [templates, setTemplates] = useState([]);
  const [selectedTpl, setSelectedTpl] = useState("");
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState("");
  const [policies, setPolicies] = useState([]);
  const [selectedPolicy, setSelectedPolicy] = useState("");
  const [agency, setAgency] = useState({});
  const [loading, setLoading] = useState(false);
  const [customText, setCustomText] = useState("");

  // Carica modelli quando cambia list_key
  useEffect(() => {
    setSelectedTpl("");
    setCustomText("");
    api.get(`/lookup-lists/${listKey}`).then(({ data }) => setTemplates(data || [])).catch(() => setTemplates([]));
  }, [listKey]);

  // Carica clienti + agenzia una volta
  useEffect(() => {
    api.get("/clients?kind=agency").then(({ data }) => setClients(data || [])).catch(() => setClients([]));
    api.get("/agency-settings").then(({ data }) => setAgency(data || {})).catch(() => setAgency({}));
  }, []);

  // Carica polizze quando cambia cliente
  useEffect(() => {
    setSelectedPolicy("");
    if (!selectedClient) { setPolicies([]); return; }
    setLoading(true);
    api.get("/policies", { params: { client_id: selectedClient } })
      .then(({ data }) => setPolicies(data || []))
      .catch(() => setPolicies([]))
      .finally(() => setLoading(false));
  }, [selectedClient]);

  const selectedTemplateText = useMemo(() => {
    if (customText) return customText;
    const t = templates.find((x) => x.id === selectedTpl);
    return t?.label || "";
  }, [customText, templates, selectedTpl]);

  const clientObj = useMemo(() => clients.find((c) => c.id === selectedClient), [clients, selectedClient]);
  const policyObj = useMemo(() => policies.find((p) => p.id === selectedPolicy), [policies, selectedPolicy]);

  const rendered = useMemo(() => {
    let out = selectedTemplateText || "";
    if (!out) return "";
    const fullName = clientObj ? `${clientObj.name || ""} ${clientObj.surname || ""}`.trim() : "—";
    const addrParts = [agency.address, agency.cap, agency.city, agency.province].filter(Boolean);
    const addr = addrParts.join(" ") || "—";
    const fmtDate = (iso) => {
      if (!iso) return "—";
      try { const d = new Date(iso); return d.toLocaleDateString("it-IT"); } catch { return iso; }
    };
    const replacements = {
      "{cliente}": fullName || "—",
      "{agenzia}": agency.legal_name || agency.name || "—",
      "{telefono_agenzia}": agency.phone || "—",
      "{indirizzo_agenzia}": addr,
      "{polizza}": policyObj?.policy_number || "—",
      "{compagnia}": policyObj?.company || "—",
      "{data_scadenza}": fmtDate(policyObj?.end_date),
      "{tipo_polizza}": policyObj?.type || "—",
      "{data_sinistro}": "—", // n/a nel test rapido
    };
    for (const [k, v] of Object.entries(replacements)) {
      out = out.split(k).join(v);
    }
    return out;
  }, [selectedTemplateText, clientObj, policyObj, agency]);

  const needsPolicy = listKey === "policy_message_templates" || listKey === "claim_invite_templates";

  const copyRendered = async () => {
    if (!rendered) return;
    try {
      await navigator.clipboard.writeText(rendered);
      toast.success("Testo copiato negli appunti");
    } catch (e) { toast.error(formatApiError(e) || "Impossibile copiare"); }
  };

  return (
    <div className="bg-gradient-to-br from-emerald-50 to-sky-50 border border-emerald-200 rounded-xl p-4 mb-4" data-testid="quick-test-panel">
      <div className="flex items-center gap-2 mb-3">
        <Play className="h-4 w-4 text-emerald-700" />
        <h2 className="text-base font-semibold text-emerald-900">Test rapido — Anteprima con dati reali</h2>
      </div>
      <p className="text-xs text-slate-600 mb-3">
        Simula un invio: seleziona modello, cliente e polizza per vedere il testo finale renderizzato.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div>
          <label className="text-[10px] uppercase tracking-wider text-slate-600 font-semibold">Tipo modello</label>
          <select value={listKey} onChange={(e) => setListKey(e.target.value)} className="h-9 w-full px-2 border border-slate-300 rounded-md text-sm bg-white" data-testid="test-tpl-type">
            <option value="client_message_templates">💬 Messaggi cliente</option>
            <option value="policy_message_templates">📄 Messaggi polizza</option>
            <option value="claim_invite_templates">⚠️ Inviti sinistro</option>
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-slate-600 font-semibold">Modello</label>
          <select value={selectedTpl} onChange={(e) => { setSelectedTpl(e.target.value); setCustomText(""); }} className="h-9 w-full px-2 border border-slate-300 rounded-md text-sm bg-white" data-testid="test-tpl">
            <option value="">— seleziona —</option>
            {templates.map((t) => <option key={t.id} value={t.id}>{t.code}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-slate-600 font-semibold">Cliente</label>
          <select value={selectedClient} onChange={(e) => setSelectedClient(e.target.value)} className="h-9 w-full px-2 border border-slate-300 rounded-md text-sm bg-white" data-testid="test-client">
            <option value="">— seleziona —</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.surname} {c.name}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-slate-600 font-semibold">
            Polizza {needsPolicy ? <span className="text-rose-500 font-normal">*</span> : <span className="text-slate-400 font-normal">(opzionale)</span>}
          </label>
          <select value={selectedPolicy} onChange={(e) => setSelectedPolicy(e.target.value)} disabled={!selectedClient} className="h-9 w-full px-2 border border-slate-300 rounded-md text-sm bg-white disabled:bg-slate-100" data-testid="test-policy">
            <option value="">{loading ? "Caricamento…" : selectedClient ? "— seleziona —" : "Prima scegli il cliente"}</option>
            {policies.map((p) => <option key={p.id} value={p.id}>{p.policy_number || p.id.slice(0,8)} · {p.company || "—"}</option>)}
          </select>
        </div>
      </div>

      <div className="mt-3">
        <div className="flex items-center justify-between mb-1">
          <label className="text-[10px] uppercase tracking-wider text-slate-600 font-semibold">Testo del modello (modificabile per prova)</label>
          {selectedTpl && !customText && <span className="text-[10px] text-slate-500">— caricato dal modello</span>}
        </div>
        <textarea
          value={selectedTemplateText}
          onChange={(e) => setCustomText(e.target.value)}
          placeholder="Seleziona un modello per pre-riempire, oppure scrivi qui un testo con {cliente}, {polizza}, ..."
          rows={3}
          className="w-full text-sm border border-slate-300 rounded-md p-2 bg-white font-mono"
          data-testid="test-template-text"
        />
      </div>

      <div className="mt-3 rounded-lg border-2 border-dashed border-emerald-300 bg-white p-3" data-testid="test-preview">
        <div className="flex items-center justify-between mb-1">
          <label className="text-[10px] uppercase tracking-wider text-emerald-800 font-semibold">Anteprima renderizzata</label>
          {rendered && (
            <button type="button" onClick={copyRendered} className="text-xs text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1" data-testid="test-copy-rendered">
              <Copy className="h-3 w-3" /> Copia
            </button>
          )}
        </div>
        <div className="text-sm text-slate-800 whitespace-pre-wrap min-h-[60px]">
          {loading ? <Loader2 className="h-4 w-4 animate-spin text-slate-400" /> : (rendered || <span className="text-slate-400 italic">Seleziona un modello e un cliente per vedere l&apos;anteprima…</span>)}
        </div>
      </div>
    </div>
  );
}

export default function MessageTemplates() {
  return (
    <div className="max-w-6xl mx-auto px-4 py-6" data-testid="message-templates-page">
      <div className="mb-6">
        <h1 className="text-2xl font-display font-semibold text-slate-900 flex items-center gap-2">
          <MessageSquareText className="h-6 w-6 text-indigo-600" /> Gestione Modelli Messaggi
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Personalizza i testi predefiniti per Email e WhatsApp che invii ai clienti dalle sezioni
          Polizze e Clienti. Usa i placeholder <code className="text-indigo-700 bg-indigo-50 rounded px-1">{"{cliente}"}</code>,
          {" "}<code className="text-indigo-700 bg-indigo-50 rounded px-1">{"{polizza}"}</code>,
          {" "}<code className="text-indigo-700 bg-indigo-50 rounded px-1">{"{compagnia}"}</code>, ecc. — il sistema li sostituirà automaticamente.
        </p>
      </div>

      <QuickTestPanel />

      <CollapsiblePanel
        emoji="💬"
        title="Messaggi cliente (generici)"
        subtitle="Auguri, appuntamenti, comunicazioni generiche — WhatsApp ed Email."
        testid="panel-client-messages"
        defaultOpen
      >
        <PlaceholdersLegend items={CLIENT_PLACEHOLDERS} />
        <LookupListEditor
          listKey="client_message_templates"
          emoji="💬"
          title="Modelli generici"
          description="Testi predefiniti per messaggi generici al cliente (non legati a una polizza specifica)."
          codePlaceholder="es. auguri"
          labelPlaceholder="Testo del messaggio (usa {cliente}, {agenzia}, ...)"
          labelMultiline
          labelRows={5}
        />
      </CollapsiblePanel>

      <CollapsiblePanel
        emoji="📄"
        title="Messaggi polizza"
        subtitle="Rinnovo, pagamento rata, scadenze, richiesta documenti — collegati a una polizza."
        testid="panel-policy-messages"
        defaultOpen
      >
        <PlaceholdersLegend items={POLICY_PLACEHOLDERS} />
        <LookupListEditor
          listKey="policy_message_templates"
          emoji="📄"
          title="Modelli polizza"
          description="Testi predefiniti per messaggi legati a una polizza (rinnovo, rata in scadenza, ecc.)."
          codePlaceholder="es. rinnovo"
          labelPlaceholder="Testo del messaggio (usa {cliente}, {polizza}, {compagnia}, {data_scadenza}, ...)"
          labelMultiline
          labelRows={5}
        />
      </CollapsiblePanel>

      <CollapsiblePanel
        emoji="⚠️"
        title="Inviti denuncia sinistro"
        subtitle="Testi predefiniti per invitare il cliente in agenzia dopo un sinistro."
        testid="panel-claim-messages"
      >
        <PlaceholdersLegend items={CLAIM_PLACEHOLDERS} />
        <LookupListEditor
          listKey="claim_invite_templates"
          emoji="⚠️"
          title="Inviti sinistro"
          description="Testi predefiniti per invito cliente dopo un sinistro."
          codePlaceholder="es. standard"
          labelPlaceholder="Testo dell'invito (usa {cliente}, {polizza}, {compagnia}, {data_sinistro}, ...)"
          labelMultiline
          labelRows={5}
        />
      </CollapsiblePanel>

      <div className="mt-6 rounded-xl border border-slate-200 bg-white p-4">
        <h3 className="text-sm font-semibold text-slate-800 mb-2 flex items-center gap-1.5">
          <Info className="h-4 w-4 text-indigo-600" /> Come vengono usati questi modelli
        </h3>
        <ul className="text-xs text-slate-600 space-y-1 list-disc list-inside">
          <li>Nella tabella Polizze clicca l&apos;icona <strong>💬 Messaggi</strong> su una riga → si apre il selettore modelli.</li>
          <li>Nella sezione Clienti, apri la scheda cliente e usa <strong>Invia messaggio</strong>.</li>
          <li>Nei Sinistri, quando invii un invito al cliente puoi scegliere un modello e/o personalizzarlo al volo.</li>
          <li>Il modello viene sempre <strong>prevalidato</strong> con i dati reali: puoi controllare l&apos;anteprima prima dell&apos;invio.</li>
        </ul>
      </div>
    </div>
  );
}
